import { FC, useEffect } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  VStack,
  DateInput,
  ModalBody,
  RedAsterix,
} from '@wegroup/design-system';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Change } from '../../../../../../../types/Form';
import CurrencyDurationRightAddon from '@advisory-report/src/features/creation-flow/components/insurance-policies/shared/modal/CurrencyDurationRightAddon';
import CollectionMethodSelectInput from '@flows/src/components/flow-products/quotes/basket/CollectionMethodSelectInput';

type Fields = 'start_date' | 'payment_interval' | 'anva_collection_method_code';

interface Props {
  hiddenFields?: Fields[];
  type?: 'POLICY' | 'PACKAGE';
  isPaymentMethodRequired?: boolean;
}

const PackageItemEditModalBody: FC<Props> = ({
  hiddenFields,
  type,
  isPaymentMethodRequired = false,
}) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();

  // Watch for payment interval changes
  const paymentInterval = watch('payment_interval');

  // Check if direct debit is required (monthly payments)
  const isDirectDebitRequired =
    paymentInterval && paymentInterval === 'MONTHLY';

  useEffect(() => {
    if (isDirectDebitRequired) {
      // Set appropriate direct debit collection method based on type
      const directDebitCode = type === 'PACKAGE' ? '48' : '28';
      setValue('anva_collection_method_code', directDebitCode);
    }
  }, [paymentInterval, setValue, type, isDirectDebitRequired]);

  const handleChangeIncasso = (value: string) => {
    setValue('anva_collection_method_code', value, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <ModalBody as={VStack} align="stretch" spacing="4">
      {!hiddenFields?.includes('start_date') && (
        <Controller
          name="start_date"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl isInvalid={!!error}>
              <FormLabel>
                {t(
                  'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_START-DATE',
                )}
              </FormLabel>
              <DateInput
                key={value || 'empty'} // Force re-render when value changes
                onChange={({ value }: Change) => onChange(value)}
                initialValue={value}
              />
              {error?.message && (
                <FormErrorMessage>{t(error.message)}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
      )}
      {!hiddenFields?.includes('payment_interval') && (
        <Controller
          name="payment_interval"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl isInvalid={!!error}>
              <FormLabel>{t('Payment interval')}</FormLabel>
              <CurrencyDurationRightAddon
                value={value}
                onChange={(e) => onChange(e.currentTarget.value)}
                borderLeft={undefined}
                borderRight={undefined}
                borderTopLeftRadius={undefined}
                borderBottomLeftRadius={undefined}
                excludedValues={['ONCE']}
              />
              {error?.message && (
                <FormErrorMessage>{t(error.message)}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
      )}
      {!hiddenFields?.includes('anva_collection_method_code') && (
        <Controller
          name="anva_collection_method_code"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl isInvalid={!!error}>
              <FormLabel>
                {t('COLLECTION-METHOD')}{' '}
                {isPaymentMethodRequired && <RedAsterix />}
              </FormLabel>
              <CollectionMethodSelectInput
                key={value ?? 'incasso-select'}
                value={value?.toString()}
                onChange={(e) => handleChangeIncasso(e.currentTarget.value)}
                type={type}
                isDisabled={isDirectDebitRequired}
              />
              {error?.message && (
                <FormErrorMessage>{t(error.message)}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
      )}
    </ModalBody>
  );
};

export default PackageItemEditModalBody;
